import { ReactNode, useEffect, useMemo } from 'react'
import {
  CargoType,
  initialQueryState,
  OptionsHelper,
  useDebounce,
} from '../../../../../../../_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  Input,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useT } from '_metronic/i18n/Metronici18n'

type Props = {
  /** Conteúdo à direita da linha dos campos (ex.: "Adicionar" ou barra de seleção em lote). */
  actions?: ReactNode
}

export const FuncionariosListSearchComponent = ({ actions }: Props) => {
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { t } = useT()
  const total = response?.totalElements ?? 0

  const debouncedFilters = useDebounce(filters, 500)

  const search = useMemo(() => {
    return [
      debouncedFilters.empresa ? `empresa:${debouncedFilters.empresa.value}` : '',
      debouncedFilters.cargo ? `cargo:${debouncedFilters.cargo.value}` : '',
      debouncedFilters.nome ? `pessoa.nome~*${debouncedFilters.nome}*` : '',
      debouncedFilters.status ? `ativo:${debouncedFilters.status.value}` : '',
    ]
      .filter(Boolean)
      .join(',')
  }, [debouncedFilters])

  useEffect(() => {
    updateState({
      search,
      ...initialQueryState,
    })
  }, [search, updateState])

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo.
  const activeFilters: ActiveFilter[] = [
    filters.nome && {
      key: 'nome',
      label: t('funcionario.label'),
      value: `“${filters.nome}”`,
      onRemove: () => setFilter('nome', ''),
    },
    filters.empresa && {
      key: 'empresa',
      label: t('empresa.label'),
      value: filters.empresa.label,
      onRemove: () => setFilter('empresa', undefined),
    },
    filters.cargo && {
      key: 'cargo',
      label: t('cols.cargo'),
      value: filters.cargo.label,
      onRemove: () => setFilter('cargo', undefined),
    },
    filters.status && {
      key: 'status',
      label: t('situacao.label'),
      value: filters.status.label,
      onRemove: () => setFilter('status', undefined),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  return (
    <TableFilters
      actions={actions}
      // Cargo e situação ficam recolhidos; aplicados, aparecem nos chips de filtros ativos.
      moreFilters={
        <>
          <TableFilter label={t('cols.cargo')}>
            <CustomSelect
              entity='cargo'
              fetchOptions={OptionsHelper.getOptionsCargos}
              onChange={(cargo?: IOption<CargoType>) => setFilter('cargo', cargo ?? undefined)}
              value={filters.cargo}
            />
          </TableFilter>
          <TableFilter label={t('situacao.label')}>
            <CustomSelect
              entity='situacao'
              options={OptionsHelper.statusCadastro}
              onChange={(status?: IOption<boolean>) => setFilter('status', status ?? undefined)}
              value={filters.status}
            />
          </TableFilter>
        </>
      }
      moreFiltersActiveCount={[filters.cargo, filters.status].filter(Boolean).length}
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isLoading
              ? 'Buscando…'
              : `${total} ${total === 1 ? 'funcionário encontrado' : 'funcionários encontrados'}`
          }
        />
      }
    >
      <TableFilter label={t('funcionario.label')} wide>
        <Input
          entity='nome'
          value={filters.nome}
          onChange={(e) => setFilter('nome', e.target.value)}
        />
      </TableFilter>
      <TableFilter label={t('empresa.label')}>
        <CustomSelect
          entity='empresa'
          onChange={(empresa?: IOption<number>) => setFilter('empresa', empresa ?? undefined)}
          fetchOptions={OptionsHelper.getOptionsEmpresas}
          value={filters.empresa}
        />
      </TableFilter>
    </TableFilters>
  )
}
